import {BookingManagementStage} from 'modules/bookingManagement/bookingManagementStage'
import {getBookingManagementStage} from 'modules/bookingManagement/selectors'
import {setBookingManagementStage} from 'modules/bookingManagement/slice'
import {getAnalyticsPageName} from 'modules/meta/selectors'

import {setCookie} from '@vio/core/src/_web/utils/cookies'
import {getServerErrorContext} from '@vio/core/src/analytics/contexts/getServerErrorContext'
import {trackEvent as trackEventV2} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {
  Category as ErrorCategory,
  ErrorType
} from '@vio/core/src/api-types/bovio/response/error'
import {makeCookieConfiguration} from '@vio/core/src/auth/business/persistData'
import {
  getIsAuthenticated,
  selectAuthEnabled
} from '@vio/core/src/auth/modules/selectors'
import {BOVIO_OTP_COOKIE} from '@vio/core/src/booking/constants'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {otpApi} from '@vio/core/src/booking/services/otpApi'

import {MiddlewareType} from '../middlewareList'

const bookingManagementMiddleware: MiddlewareType = store => next => action => {
  const {dispatch, getState} = store
  const state = getState()
  const page = getAnalyticsPageName(state)
  const isAuthenticated = getIsAuthenticated(state)
  const canSignIn = () => !isAuthenticated && selectAuthEnabled(state)
  const previousStage = getBookingManagementStage(state)
  const team = Team.Checkout

  if (bookingRetrieveApi.endpoints.getBookingById.matchPending(action)) {
    dispatch(setBookingManagementStage(BookingManagementStage.Loading))
  }

  if (bookingRetrieveApi.endpoints.getBookingById.matchFulfilled(action)) {
    dispatch(setBookingManagementStage(BookingManagementStage.Granted))
  }

  if (bookingRetrieveApi.endpoints.getBookingById.matchRejected(action)) {
    const bookingError = action?.payload
    const errorType = bookingError?.data?.type
    const isOtpRequired =
      errorType === ErrorType.Otp || errorType === ErrorType.BookingAccessDenied

    if (!isOtpRequired)
      dispatch(
        trackEventV2({
          category: Category.System,
          entity: Entity.Page,
          action: Action.Errored,
          page,
          team,
          analyticsContext: {
            [AnalyticsContext.ServerErrorContext]:
              getServerErrorContext(bookingError)
          }
        })
      )
    if (isOtpRequired) {
      dispatch(setBookingManagementStage(BookingManagementStage.OtpPreSend))
    } else if (errorType === ErrorType.BookingNotFound) {
      dispatch(setBookingManagementStage(BookingManagementStage.NotFound))
    } else {
      dispatch(setBookingManagementStage(BookingManagementStage.Error))
    }
  }

  if (otpApi.endpoints.requestOtp.matchFulfilled(action)) {
    dispatch(setBookingManagementStage(BookingManagementStage.OtpVerification))
  }

  if (otpApi.endpoints.requestOtp.matchRejected(action)) {
    const errorType = String(action?.payload?.data?.type || '')
    const errorDetails = action?.payload?.data?.details
    const errorCategory = errorDetails?.category || ''
    const analyticsContext = {
      [AnalyticsContext.ErrorContext]: {errorType, errorDetails}
    }
    const isResend = previousStage !== BookingManagementStage.OtpPreSend

    if (isResend)
      dispatch(
        trackEventV2({
          category: Category.System,
          entity: Entity.OtpResend,
          action: Action.Errored,
          page,
          team,
          analyticsContext,
          payload: {fromStage: previousStage}
        })
      )
    if (errorCategory === ErrorCategory.OtpCooldown) {
      // A code was already sent recently; show the code-entry screen and let
      // the resend timer resume from retryAfter (via getOtpRequestData).
      dispatch(
        setBookingManagementStage(BookingManagementStage.OtpVerification)
      )
    } else if (canSignIn())
      dispatch(setBookingManagementStage(BookingManagementStage.SignIn))
    else if (errorCategory === ErrorCategory.OtpRateLimited) {
      dispatch(setBookingManagementStage(BookingManagementStage.AccessDenied))
    } else dispatch(setBookingManagementStage(BookingManagementStage.Error))
  }

  if (otpApi.endpoints.verifyOtp.matchFulfilled(action)) {
    const {accessToken, expiresIn} = action.payload
    setCookie(BOVIO_OTP_COOKIE, accessToken, {
      ...makeCookieConfiguration(),
      expires: new Date(new Date().getTime() + expiresIn * 1000)
    })
  }

  if (otpApi.endpoints.verifyOtp.matchRejected(action)) {
    const errorDetails = action?.payload?.data?.details
    const errorCategory = errorDetails?.category || ''
    if (
      errorCategory === ErrorCategory.OtpExpiredCode ||
      errorCategory === ErrorCategory.OtpNotFound
    )
      dispatch(setBookingManagementStage(BookingManagementStage.OtpExpired))
    if (errorCategory === ErrorCategory.OtpMaxAttemptsExceeded) {
      if (canSignIn())
        dispatch(setBookingManagementStage(BookingManagementStage.SignIn))
      else {
        dispatch(setBookingManagementStage(BookingManagementStage.AccessDenied))
      }
    }
  }

  return next(action)
}

export default bookingManagementMiddleware
